import { isArray, isPlainObject } from 'lodash';

/**
 * The lists the backend treats as the source of truth on every update: an empty one means "delete
 * all", so an object holding one of them is never empty.
 */
const SYNCED_LISTS = ['goodsItem', 'consignmentHouseLevel'];

const holdsSyncedList = (value: Record<string, any>) => SYNCED_LISTS.some((key) => isArray(value[key]));

const isEmptyList = (value: any) => isArray(value) && value.length === 0;

/** True for an object with nothing in it but empty lists, `{}` included. */
const holdsNothingButEmptyLists = (value: any) => {
    if (!isPlainObject(value) || holdsSyncedList(value)) return false;

    return Object.values(value).every(isEmptyList);
};

/**
 * Drops the objects, nested anywhere below the root, that hold nothing but empty lists.
 *
 * `removeEmptyObjects` keeps empty lists, so `{ communication: [] }` survives it. The backend writes
 * nothing for the empty list and is left with an empty element, which customs rejects when the element
 * has mandatory children - an empty `representative` was rejected that way (IE3N99
 * `cvc-complex-type.2.4.b`). It runs after `removeEmptyObjects`, so a list whose entries were all
 * blank has already been reduced to `[]`.
 *
 * The root is always kept, and so is any object holding the synced lists.
 */
export const withoutEmptyElements = <T>(value: T): T => {
    if (isArray(value)) {
        const cleanedEntries = value.map((entry) => withoutEmptyElements(entry));
        return cleanedEntries.filter((entry) => !holdsNothingButEmptyLists(entry)) as unknown as T;
    }

    if (!isPlainObject(value)) return value;

    const cleanedEntries = Object.entries(value as Record<string, any>)
        .map(([key, child]) => [key, withoutEmptyElements(child)] as const)
        .filter(([, child]) => !holdsNothingButEmptyLists(child));

    return Object.fromEntries(cleanedEntries) as T;
};
